import type { PayloadAction } from '@reduxjs/toolkit';
import type { SagaIterator } from 'redux-saga';
import { select, takeEvery } from 'redux-saga/effects';

import { institutionLoginsActions, institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { rootUiActions, rootUiSelectors } from 'app-components/flux/root-ui';
import { getLogger } from 'app-components/utils/core';
import type { RecordOf } from 'immutable';

import {
  getReauthDialogId,
  showReauthenticationDialog,
} from 'components/Dialogs/AccountDiscovery/ReauthDialog/dispatcher';

const log = getLogger('data/institutionLogins/pauthReauthSagas');

// Track institution logins we've already shown dialogs for to avoid duplicates
const activeDialogs = new Set<string>();

function* handleDialogRemoved(action: PayloadAction<string>) {
  const dialogId = action.payload;

  // Check if this is one of our reauth dialogs
  if (dialogId?.startsWith(getReauthDialogId())) {
    const institutionLoginId = dialogId.replace(getReauthDialogId(), '');
    if (activeDialogs.has(institutionLoginId)) {
      log.info('Clearing activeDialogs for closed dialog:', institutionLoginId);
      activeDialogs.delete(institutionLoginId);
    }
  }
}

function* handlePauthInvalidTokenDetected(
  action: PayloadAction<{ institutionLoginId: string; previousStatus?: string }>,
): SagaIterator {
  const { institutionLoginId, previousStatus } = action.payload;

  log.info(
    'Handling PAUTH_INVALID_TOKEN for institution login:',
    institutionLoginId,
    'Previous status:',
    previousStatus,
  );

  try {
    const institutionLogin: ReturnType<typeof institutionLoginsSelectors.getInstitutionLoginForId> = yield select(
      (state) => institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId),
    );

    if (!institutionLogin) {
      log.warn('Institution login not found:', institutionLoginId);
      return;
    }

    if (institutionLogin.refreshFrequency === 'NEVER') {
      log.info('Skipping ReauthDialog - refresh frequency is NEVER for institution login:', institutionLoginId);
      return;
    }

    const dialogId = getReauthDialogId(institutionLoginId);
    const activeModals: ReturnType<typeof rootUiSelectors.getActiveModals> = yield select(
      rootUiSelectors.getActiveModals,
    );

    const existingDialog = activeModals?.find((modal: RecordOf<{ id: string }>) => modal?.id === dialogId);

    if (existingDialog) {
      log.info('Skipping ReauthDialog - dialog already open for this institution:', institutionLoginId);
      return;
    }

    // Self-healing: if activeDialogs has a stale entry but Redux doesn't show the dialog, clear it
    if (activeDialogs.has(institutionLoginId) && !existingDialog) {
      log.info('Clearing stale activeDialogs entry for institution:', institutionLoginId);
      activeDialogs.delete(institutionLoginId);
    }

    activeDialogs.add(institutionLoginId);

    log.info('Showing ReauthDialog for institution login:', institutionLoginId);

    showReauthenticationDialog({ institutionLoginId });
  } catch (error) {
    log.error('Error handling PAUTH_INVALID_TOKEN:', error);
    activeDialogs.delete(institutionLoginId);
  }
}

export function* pauthReauthWatcher() {
  log.info('pauthReauthWatcher started, listening for:', institutionLoginsActions.pauthInvalidTokenDetected.type);
  yield takeEvery(institutionLoginsActions.pauthInvalidTokenDetected.type, handlePauthInvalidTokenDetected);
}

export function* dialogRemovalWatcher() {
  yield takeEvery(rootUiActions.removeDialog.type, handleDialogRemoved);
}

export default [pauthReauthWatcher, dialogRemovalWatcher];
